// InputsParamsTable.js (cloud-admin-portal)
//
// Shared editor for a pipeline's inputs & parameters, used both by the OTS
// import wizard (step 2) and by the post-import "Configure" editor — so a
// parameter's DESCRIPTION is displayed and editable both at import time and
// later. Each row is an INPUT (a file uploaded at launch) or a PARAMETER
// (a value set at launch). Descriptions are optional free text shown to users.
import React from "react";
import "./ImportWizard.css";

// Merge the parser's separate inputs[]/parameters[] into combined rows for a
// FRESH import — every detected row defaults to "parameter" (the admin opts
// rows into "input"); required defaults off.
export function mergeDetected(inputs, parameters) {
    return [...(inputs || []), ...(parameters || [])].map((x) => ({
        kind: "param",
        name: x.name || "",
        flag: x.flag || "",
        required: false,
        alwaysInclude: !!x.alwaysInclude,
        description: x.description || "",
        extensions: Array.isArray(x.extensions) ? x.extensions.join(",") : (x.extensions || ""),
        type: x.type || "string",
        default: x.default == null ? "" : x.default,
        choices: Array.isArray(x.choices) ? x.choices.join(",") : (x.choices || ""),
    })).sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
}

// Build combined rows from ALREADY-SAVED inputs[]/parameters[] (post-import
// edit), preserving each row's kind, required flag, type, default and
// description.
export function combineSaved(inputs, parameters) {
    return [
        ...(inputs || []).map((x) => ({
            kind: "input", name: x.name || "", flag: x.flag || "", required: !!x.required,
            description: x.description || "",
            extensions: Array.isArray(x.extensions) ? x.extensions.join(",") : (x.extensions || ""),
            type: "string", default: "", choices: "",
        })),
        ...(parameters || []).map((x) => ({
            kind: "param", name: x.name || "", flag: x.flag || "", required: !!x.required,
            alwaysInclude: !!x.alwaysInclude,
            description: x.description || "", extensions: "",
            type: x.type || "string", default: x.default == null ? "" : x.default,
            choices: Array.isArray(x.choices) ? x.choices.join(",") : (x.choices || ""),
        })),
    ];
}

// Split combined rows back into inputs[]/parameters[] (what the launch path,
// validator and launch page consume). Descriptions are carried through.
export function splitValues(values) {
    return {
        inputs: values.filter((v) => v.kind === "input").map((v) => ({
            name: v.name, flag: v.flag, required: v.required,
            extensions: String(v.extensions || "").split(",").map((x) => x.trim()).filter(Boolean),
            description: v.description,
        })),
        parameters: values.filter((v) => v.kind === "param").map((v) => ({
            name: v.name, flag: v.flag, type: v.type, default: v.default, required: v.required,
            alwaysInclude: !!v.alwaysInclude,
            description: v.description,
            choices: String(v.choices || "").split(",").map((c) => c.trim()).filter(Boolean),
        })),
    };
}

export default function InputsParamsTable({ values, setValues }) {
    const upd = (i, k, v) => setValues((a) => a.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
    const addInput = () => setValues((a) => [...a, { kind: "input", name: "", flag: "", required: false, alwaysInclude: false, description: "", extensions: "", type: "string", default: "", choices: "" }]);
    const addParam = () => setValues((a) => [...a, { kind: "param", name: "", flag: "", required: false, alwaysInclude: false, description: "", extensions: "", type: "string", default: "", choices: "" }]);
    const removeRow = (i) => setValues((a) => a.filter((_, idx) => idx !== i));
    const loadParamsJson = (file) => {
        const rd = new FileReader();
        rd.onload = () => {
            try {
                const arr = JSON.parse(String(rd.result || "[]"));
                if (!Array.isArray(arr)) return;
                setValues((a) => [...a, ...arr.map((p) => ({ kind: "param", name: p.name || "", flag: p.flag || (p.name ? "--" + p.name : ""), required: !!p.required, alwaysInclude: !!p.alwaysInclude, description: p.description || "", extensions: "", type: p.type || "string", default: p.default == null ? "" : p.default, choices: Array.isArray(p.choices) ? p.choices.join(",") : (p.choices || "") }))]);
            } catch (_) { /* ignore malformed file */ }
        };
        rd.readAsText(file);
    };

    return (
        <>
            <table className="iw-table">
                <thead><tr><th>Kind</th><th>Name</th><th>Flag</th><th>Required</th><th title="Always include this parameter in the launch command, even at its default value (for a boolean, always emit the flag)">Add to Command</th><th>Details</th><th>Description</th><th></th></tr></thead>
                <tbody>
                    {values.map((r, i) => (
                        <tr key={i}>
                            <td>
                                <select className="iw-select" value={r.kind} onChange={(e) => upd(i, "kind", e.target.value)}>
                                    <option value="input">Input (file)</option>
                                    <option value="param">Parameter</option>
                                </select>
                            </td>
                            <td><input className="iw-input" value={r.name} onChange={(e) => upd(i, "name", e.target.value)} /></td>
                            <td><input className="iw-input" value={r.flag} onChange={(e) => upd(i, "flag", e.target.value)} placeholder={"--" + (r.name || "flag")} /></td>
                            <td style={{ textAlign: "center" }}><input type="checkbox" checked={!!r.required} onChange={(e) => upd(i, "required", e.target.checked)} /></td>
                            <td style={{ textAlign: "center" }}>
                                {r.kind === "param"
                                    ? <input type="checkbox" checked={!!r.alwaysInclude} onChange={(e) => upd(i, "alwaysInclude", e.target.checked)} title="Always include in the launch command" />
                                    : <span style={{ color: "#cbd5e1" }}>—</span>}
                            </td>
                            <td>
                                {r.kind === "param" ? (
                                    <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", alignItems: "center" }}>
                                        <select className="iw-select" style={{ width: 104, flex: "0 0 auto" }} value={r.type} onChange={(e) => upd(i, "type", e.target.value)}>
                                            {["string", "number", "boolean", "enum", "list", "path"].map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        {(r.type === "enum" || r.type === "list") && (
                                            <input className="iw-input" style={{ width: 130, flex: "0 0 auto" }} value={r.choices || ""} onChange={(e) => upd(i, "choices", e.target.value)} placeholder="options e.g. Y,N" />
                                        )}
                                        {r.type !== "boolean" && (
                                            <input className="iw-input" style={{ flex: "1 1 auto", width: "auto", minWidth: 90 }} value={r.default} onChange={(e) => upd(i, "default", e.target.value)} placeholder={r.type === "enum" ? "default (one of options)" : r.type === "list" ? "default e.g. a,b,c" : r.type === "path" ? "https://… or s3://…" : "value (editable at launch)"} />
                                        )}
                                    </div>
                                ) : (
                                    <input className="iw-input" value={r.extensions || ""} onChange={(e) => upd(i, "extensions", e.target.value)} placeholder="extensions e.g. fastq,gz" />
                                )}
                            </td>
                            <td><input className="iw-input" value={r.description || ""} onChange={(e) => upd(i, "description", e.target.value)} placeholder="shown to users (optional)" /></td>
                            <td><button className="iw-btn ghost sm" onClick={() => removeRow(i)}>✕</button></td>
                        </tr>
                    ))}
                    {values.length === 0 && <tr><td colSpan={8} style={{ color: "#94a3b8" }}>No values — add inputs/parameters below.</td></tr>}
                </tbody>
            </table>
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button className="iw-btn ghost sm" onClick={addInput}>+ Add Input</button>
                <button className="iw-btn ghost sm" onClick={addParam}>+ Add Parameter</button>
                <label className="iw-link" style={{ marginLeft: "auto" }}>Upload params.json<input type="file" accept=".json" style={{ display: "none" }} onChange={(e) => e.target.files[0] && loadParamsJson(e.target.files[0])} /></label>
            </div>
        </>
    );
}
